import { useNotifications } from '../../contexts/NotificationContext';
import { useUIState } from '../../contexts/UIStateContext';
import NoProductsBanner from '../knowledge/NoProductsBanner';
import AIConnectionBanner from '../knowledge/AIConnectionBanner';
import ImportProgressBanner from '../knowledge/ImportProgressBanner';

/**
 * NotificationBanners - Centralized component for rendering notification banners
 *
 * This component provides a single source of truth for notification-to-banner mapping.
 * It filters for actionable notifications and renders the appropriate banner component
 * for each notification type.
 *
 * @param {Object} props
 * @param {Function} props.onCreateProduct - Callback when user clicks to create a product (for no_products notification)
 * @param {Function} props.onRefresh - Callback to refresh data after import completes
 * @returns {JSX.Element} Rendered notification banners
 */
export default function NotificationBanners({ onCreateProduct, onRefresh }) {
  const { notifications, showModal } = useNotifications();
  const { getKnowledgeImportProgress } = useUIState();
  const importProgress = getKnowledgeImportProgress();

  // Get all notifications that require action (for banners)
  const actionableNotifications = notifications.filter(
    n => n.requires_action && (n.status === 'unread' || n.status === 'read')
  );

  // Notification type to banner component mapping
  // This is the single source of truth for all notification banners
  const renderBanner = (notification) => {
    switch (notification.type) {
      case 'no_products':
        return (
          <NoProductsBanner
            key={notification.id}
            onResolve={onCreateProduct}
          />
        );

      case 'ai_connection_missing':
        return (
          <AIConnectionBanner
            key={notification.id}
            onResolve={() => showModal(notification)}
          />
        );

      // Add more notification types here as needed
      // case 'new_type':
      //   return <NewTypeBanner key={notification.id} onResolve={...} />;

      default:
        // Unknown notification type - don't render anything
        console.warn(`[NotificationBanners] Unknown notification type: ${notification.type}`);
        return null;
    }
  };

  // Check if we have any banners to show
  const hasNotifications = actionableNotifications.length > 0;
  const hasImportProgress = !!importProgress;

  // Don't render wrapper if no notifications or import progress
  if (!hasNotifications && !hasImportProgress) {
    return null;
  }

  return (
    <div className="pt-3">
      {/* Import Progress Banner (always shows first if active) */}
      {hasImportProgress && <ImportProgressBanner onRefresh={onRefresh} />}

      {/* Other notification banners */}
      {actionableNotifications.map(renderBanner)}
    </div>
  );
}
